/**
 * BoardChromeSlot — the composable chrome for a board. Reads the surrounding
 * `BoardProvider` substrate (focal + commands) and renders the right chrome for
 * the form factor: the desktop `BoardChrome` bar or the phone `MobileBoardHeader`.
 * The board-owned smart-view toggle is assembled here (from the operator runtime)
 * and prepended to the host's trailing actions.
 *
 * It subscribes to the VOLATILE focal context, so it re-renders on every focal
 * change — while its sibling `BoardSurface` (stable context only) does not. Keep
 * this component the ONLY focal subscriber in a board composition; the composer
 * (InfiniteBoard) passes it static config and stays focal-free, so it never
 * recreates the board element.
 *
 * `BoardChrome` and `MobileBoardHeader` stay pure/prop-driven — this slot is the
 * substrate-reading glue.
 */
import type { ReactNode } from "react";
import { useBoardCommands, useBoardFocal } from "./BoardContext";
import { BoardChrome } from "./BoardChrome";
import { MobileBoardHeader } from "./MobileBoardHeader";
import { SmartViewToggle } from "./SmartViewToggle";
import type { OverflowItem } from "../components/OverflowToolbar";

export interface BoardChromeSlotProps {
  /** Phone layout → the floating `MobileBoardHeader` instead of the desktop bar. */
  readonly mobile: boolean;
  /** Desktop: host content before the breadcrumb (the nav-panel toggle). */
  readonly leading?: ReactNode;
  /** Desktop: host action buttons (view controls); the board prepends its own
   *  smart-view toggle, then they collapse together into the "=" dropdown. */
  readonly trailingItems?: readonly OverflowItem[];
  /** Phone: full-screen settings-sheet content (render prop gets `close`). */
  readonly mobileMenu?: (close: () => void) => ReactNode;
  /** Phone: header search button → host's ⌘F modal. Absent → no button. */
  readonly onMobileSearch?: () => void;
}

export function BoardChromeSlot({ mobile, leading, trailingItems, mobileMenu, onMobileSearch }: BoardChromeSlotProps) {
  const { steps, viewport, operator } = useBoardFocal();
  const { commands } = useBoardCommands();

  if (mobile) {
    return (
      <MobileBoardHeader
        steps={steps}
        onNavigate={(i) => {
          // Root crumb resets; any other level (including current) re-frames it.
          if (i === 0) commands.resetView();
          else if (viewport) commands.navigateTo(viewport.focalPath.slice(0, i + 1));
        }}
        menu={mobileMenu}
        smartView={operator.showToggle ? { on: operator.on, onToggle: operator.toggle } : null}
        onSearch={onMobileSearch}
      />
    );
  }

  // Desktop chrome waits for the first reported viewport (its breadcrumb navigation
  // reads `viewport.focalPath`).
  if (!viewport) return null;
  return (
    <BoardChrome
      steps={steps}
      viewport={viewport}
      onPopOut={commands.popOut}
      onResetView={commands.resetView}
      onNavigateTo={commands.navigateTo}
      leading={leading}
      trailingItems={[
        // Smart view leads (leftmost → survives overflow longest); still just
        // another collapsible action, equal with the rest.
        ...(operator.showToggle
          ? [{ key: "smart-view", node: <SmartViewToggle on={operator.on} onToggle={operator.toggle} /> }]
          : []),
        ...(trailingItems ?? []),
      ]}
    />
  );
}
